import moment from "moment";
import { nanoid } from "nanoid";
import { IncomeSources } from "./incomeSources";
import { ExpandMore } from "@mui/icons-material";
import { formatPrice } from "../../../utils/formatPrice";
import { formatPhoneNumber } from "../../../utils/formatPhoneNumber";
import { convertToFormattedAmount } from "../../../utils/convertAmount";
import {
  RelationshipMapping,
  MaritalStatusMapping,
  MortgageStatusMapping,
  type AddressInterface,
  DownPaymentSourceMapping,
  type MortgagePayloadInterface,
  CoApplicantIncomeSourceMapping
} from "../../../interfaces/mortgageDetailsInterface";
import {
  Paper,
  Box,
  Grid2,
  Table,
  TableRow,
  TableCell,
  Accordion,
  TableBody,
  TableHead,
  Typography,
  TableContainer,
  AccordionDetails,
  AccordionSummary
} from "@mui/material";

interface PersonalDetailsSectionProps {
  mortgageDetails: MortgagePayloadInterface;
}

export const PersonalDetailsSection = ({
  mortgageDetails
}: PersonalDetailsSectionProps) => {
  const getDisplayValue = <T,>(
    mapping: Record<string, T>,
    value: T
  ): string => {
    const entry = Object.entries(mapping).find(
      ([_, enumValue]) => enumValue === value
    );
    return entry ? entry[0] : "N/A";
  };
  const renderAddress = (address: AddressInterface) =>
    `${address.address1}, ${address.city}, ${address.province}, ${address.postalCode}, ${address.country}`;

  const getDisplayValueForStatus = (
    statusMapping: Record<string, string>,
    status: string | undefined
  ) => {
    if (!status || !statusMapping[status]) return "N/A";

    const statusStyles: Record<string, { color: string; background: string }> =
      {
        Incomplete: { color: "#ffffff", background: "#FF5722" },
        "Pending Documents": { color: "#ffffff", background: "#FFC107" },
        "Pending Contact": { color: "#ffffff", background: "#03A9F4" },
        "Pending Approval": { color: "#ffffff", background: "#FF9800" },
        Approved: { color: "#ffffff", background: "#4CAF50" }
      };

    const { color, background } = statusStyles[statusMapping[status]] || {
      color: "#ffffff",
      background: "#9E9E9E"
    };

    return (
      <Box
        sx={{
          display: "inline-block",
          padding: "4px 8px",
          borderRadius: "16px",
          color: color,
          backgroundColor: background,
          fontSize: "0.875rem",
          fontWeight: "bold"
        }}
      >
        {statusMapping[status]}
      </Box>
    );
  };
  return (
    <Paper elevation={4} sx={{ p: 2, mt: 2, flex: "1 1 calc(50% - 16px)" }}>
      <Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            mb: 2
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="h4" component="div">
              <span style={{ color: "#FECD07" }}>
                {mortgageDetails.mortgage.firstName}{" "}
                {mortgageDetails.mortgage.lastName}
              </span>{" "}
              Mortgage Application Details
            </Typography>
            {mortgageDetails.mortgage.status && (
              <Typography
                sx={{ alignSelf: "center", textAlign: "center" }}
                component="div"
              >
                {getDisplayValueForStatus(
                  MortgageStatusMapping,
                  mortgageDetails.mortgage.status
                )}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              mt: 1,
              display: "flex",
              justifyContent: "space-between",
              flexDirection: {
                xs: "column-reverse",
                sm: "column-reverse",
                md: "column-reverse",
                lg: "row"
              }
            }}
          >
            <Box>
              <Typography
                variant="body2"
                sx={{ color: "gray" }}
                component="div"
              >
                <strong>Push Notification:</strong>{" "}
                {mortgageDetails.mortgage.pushNotification === null
                  ? "N/A"
                  : mortgageDetails.mortgage.pushNotification
                    ? "Enabled"
                    : "Disabled"}
              </Typography>
              {(mortgageDetails.mortgage.matchedListings ||
                mortgageDetails.mortgage.matchedListings === 0) && (
                <Typography
                  variant="body2"
                  sx={{ color: "gray" }}
                  component="div"
                >
                  <strong>Matched Listings:</strong>{" "}
                  {mortgageDetails.mortgage.matchedListings}
                </Typography>
              )}
              {(mortgageDetails.mortgage.likedHomes ||
                mortgageDetails.mortgage.likedHomes === 0) && (
                <Typography
                  variant="body2"
                  sx={{ color: "gray" }}
                  component="div"
                >
                  <strong>Liked Homes:</strong>{" "}
                  {mortgageDetails.mortgage.likedHomes}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography
                variant="body2"
                sx={{ color: "gray" }}
                component="div"
              >
                <strong>Created At:</strong>{" "}
                {mortgageDetails.mortgage.createdAt
                  ? new Date(mortgageDetails.mortgage.createdAt)
                      .toLocaleString("en-US", {
                        timeZone: "America/New_York",
                        weekday: "short",
                        year: "numeric",
                        month: "short",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true
                      })
                      .replace(",", "")
                      .replace(/(\d+:\d+ [APM]+)$/, "at $1")
                  : ""}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "gray" }}
                component="div"
              >
                <strong>Last Updated:</strong>{" "}
                {mortgageDetails.mortgage.updatedAt
                  ? new Date(mortgageDetails.mortgage.updatedAt)
                      .toLocaleString("en-US", {
                        timeZone: "America/New_York",
                        weekday: "short",
                        year: "numeric",
                        month: "short",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true
                      })
                      .replace(",", "")
                      .replace(/(\d+:\d+ [APM]+)$/, "at $1")
                  : ""}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6" component="div">
            Personal Information
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Paper elevation={4} sx={{ p: 2, mt: 2 }}>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Typography component="div">
                  <strong>First Name:</strong>{" "}
                  {mortgageDetails.mortgage.firstName || "N/A"}
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Typography component="div">
                  <strong>Middle Name:</strong>{" "}
                  {mortgageDetails.mortgage.middleName || "N/A"}
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Typography component="div">
                  <strong>Last Name:</strong>{" "}
                  {mortgageDetails.mortgage.lastName || "N/A"}
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Typography component="div">
                  <strong>Phone:</strong>{" "}
                  {mortgageDetails.mortgage.phoneNumber
                    ? formatPhoneNumber(mortgageDetails.mortgage.phoneNumber)
                    : "N/A"}
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Typography component="div">
                  <strong>Email:</strong>{" "}
                  {mortgageDetails.mortgage.emailAddress || "N/A"}
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Typography component="div">
                  <strong>Date of Birth:</strong>{" "}
                  {mortgageDetails.mortgage.dateOfBirth
                    ? moment(mortgageDetails.mortgage.dateOfBirth).format(
                        "YYYY-MM-DD"
                      )
                    : "N/A"}
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <Typography component="div">
                  <strong>Marital Status:</strong>{" "}
                  {mortgageDetails.mortgage.maritalStatus
                    ? getDisplayValue(
                        MaritalStatusMapping,
                        mortgageDetails.mortgage.maritalStatus
                      )
                    : "N/A"}
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <Typography component="div">
                  <strong>Address:</strong>{" "}
                  {mortgageDetails.mortgage.address?.address1
                    ? renderAddress(mortgageDetails.mortgage.address)
                    : "N/A"}
                </Typography>
              </Grid2>
            </Grid2>
          </Paper>
        </AccordionDetails>
      </Accordion>
      {mortgageDetails.mortgage.incomeSources && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6" component="div">
              Income Sources
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {IncomeSources(mortgageDetails.mortgage.incomeSources)}
          </AccordionDetails>
        </Accordion>
      )}
      {mortgageDetails.mortgage.coApplicants && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6" component="div">
              Co-Applicants
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {mortgageDetails.mortgage.coApplicants?.length ? (
              mortgageDetails.mortgage.coApplicants.map((coApplicant) => (
                <Paper elevation={2} sx={{ p: 2, mt: 2 }} key={nanoid()}>
                  <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <Typography component="div">
                        <strong>First Name:</strong>{" "}
                        {coApplicant.firstName || "N/A"}
                      </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <Typography component="div">
                        <strong>Middle Name:</strong>{" "}
                        {coApplicant.middleName || "N/A"}
                      </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <Typography component="div">
                        <strong>Last Name:</strong>{" "}
                        {coApplicant.lastName || "N/A"}
                      </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <Typography component="div">
                        <strong>Phone:</strong>{" "}
                        {formatPhoneNumber(coApplicant.phoneNumber) || "N/A"}
                      </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <Typography component="div">
                        <strong>Email:</strong>{" "}
                        {coApplicant.emailAddress || "N/A"}
                      </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <Typography component="div">
                        <strong>Date of Birth:</strong>{" "}
                        {coApplicant.dateOfBirth
                          ? moment(coApplicant.dateOfBirth).format("YYYY-MM-DD")
                          : "N/A"}
                      </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <Typography component="div">
                        <strong>Marital Status:</strong>{" "}
                        {getDisplayValue(
                          MaritalStatusMapping,
                          coApplicant.maritalStatus
                        )}
                      </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <Typography component="div">
                        <strong>Income Source:</strong>{" "}
                        {getDisplayValue(
                          CoApplicantIncomeSourceMapping,
                          coApplicant.incomeSource
                        )}
                      </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <Typography component="div">
                        <strong>Relationship:</strong>{" "}
                        {getDisplayValue(
                          RelationshipMapping,
                          coApplicant.relationship
                        )}
                      </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <Typography component="div">
                        <strong>First Time Home Buyer:</strong>{" "}
                        {coApplicant.isFirstTimeHomeBuyer ? "Yes" : "No"}
                      </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <Typography component="div">
                        <strong>Annual Base Pay:</strong> $
                        {formatPrice(
                          convertToFormattedAmount(
                            coApplicant.annualBasePay.amount
                          )
                        )}
                      </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                      <Typography component="div">
                        <strong>Address:</strong>{" "}
                        {coApplicant.address
                          ? renderAddress(coApplicant.address)
                          : "N/A"}
                      </Typography>
                    </Grid2>
                  </Grid2>
                </Paper>
              ))
            ) : (
              <Typography>No Co-Applicants</Typography>
            )}
          </AccordionDetails>
        </Accordion>
      )}
      {mortgageDetails.mortgage.downPaymentSources?.length && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography variant="h6" component="div">
              Combined Down Payment Sources
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography
                        component="div"
                        variant="subtitle2"
                        color="textSecondary"
                      >
                        Type
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        component="div"
                        variant="subtitle2"
                        color="textSecondary"
                      >
                        Amount
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mortgageDetails.mortgage.downPaymentSources.map((source) => (
                    <TableRow key={nanoid()} hover>
                      <TableCell>
                        {source.type
                          ? getDisplayValue(
                              DownPaymentSourceMapping,
                              source.type
                            )
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                        <Typography
                          color="primary"
                          fontWeight="bold"
                          component="div"
                        >
                          $
                          {formatPrice(
                            convertToFormattedAmount(source.amount.amount)
                          )}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      )}
    </Paper>
  );
};
